'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { ParticipantsButton } from './ParticipantsButton';
import { ParticipantsList } from './ParticipantsList';

export function InjectParticipantsIntoBar() {
  const [target, setTarget] = React.useState<HTMLElement | null>(null);
  const [videoConferenceContainer, setVideoConferenceContainer] =
    React.useState<HTMLElement | null>(null);
  const [isParticipantsOpen, setIsParticipantsOpen] = React.useState(false);
  const placeholderRef = React.useRef<HTMLSpanElement | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const findVideoContainer = () => {
      return document.querySelector('.lk-video-conference') as HTMLElement;
    };

    const ensurePlaceholder = () => {
      if (!placeholderRef.current) {
        const el = document.createElement('span');
        el.style.display = 'inline-flex';
        el.style.marginLeft = '6px';
        el.setAttribute('data-participants-injected', 'true');
        placeholderRef.current = el;
      }
      return placeholderRef.current!;
    };

    const tryInsert = () => {
      const bar = document.querySelector('.lk-control-bar') as HTMLElement | null;
      if (!bar) return false;

      // Estratégia 1: Tentar usar o botão de gravar como âncora
      let recordAnchor = bar.querySelector('[data-record-injected]') as HTMLElement | null;
      
      // Estratégia 2: Se não encontrar, tentar encontrar o botão de gravação nativo do LiveKit
      if (!recordAnchor) {
        recordAnchor = bar.querySelector('button[data-lk-source="record"]') as HTMLElement | null;
      }
      
      // Estratégia 3: Se ainda não encontrar, usar o último botão-group como âncora
      if (!recordAnchor) {
        const buttonGroups = bar.querySelectorAll('.lk-button-group');
        if (buttonGroups.length > 0) {
          recordAnchor = buttonGroups[buttonGroups.length - 1] as HTMLElement;
        }
      }
      
      // Estratégia 4: Fallback - inserir no final da barra
      if (!recordAnchor || !recordAnchor.parentElement) {
        const placeholder = ensurePlaceholder();
        if (!placeholder.isConnected) {
          bar.appendChild(placeholder);
        }
        setTarget(placeholder);
        return true;
      }

      const placeholder = ensurePlaceholder();
      if (!placeholder.isConnected) {
        recordAnchor.insertAdjacentElement('afterend', placeholder);
      }
      setTarget(placeholder);
      return true;
    };

    const observer = new MutationObserver(() => {
      const videoContainer = findVideoContainer();
      if (videoContainer && videoContainer !== videoConferenceContainer) {
        setVideoConferenceContainer(videoContainer);
      }

      // Tentar inserir o botão quando houver mudanças no DOM
      if (!target) {
        tryInsert();
      }
    });

    // Observar mudanças no DOM
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Tentar imediatamente e continuar observando até conseguir
    if (!tryInsert()) {
      const id = window.setInterval(() => {
        if (tryInsert()) window.clearInterval(id);
      }, 150);

      const videoContainer = findVideoContainer();
      if (videoContainer) {
        setVideoConferenceContainer(videoContainer);
      }

      return () => {
        window.clearInterval(id);
        observer.disconnect();
      };
    }

    // Set video container
    const videoContainer = findVideoContainer();
    if (videoContainer) {
      setVideoConferenceContainer(videoContainer);
    }

    return () => {
      observer.disconnect();
    };
  }, [target, videoConferenceContainer]);

  React.useEffect(() => {
    return () => {
      // Clean up placeholder
      if (placeholderRef.current && placeholderRef.current.parentElement) {
        placeholderRef.current.parentElement.removeChild(placeholderRef.current);
      }
    };
  }, []);

  const handleToggleParticipants = React.useCallback(() => {
    setIsParticipantsOpen((prev) => !prev);
  }, []);

  const handleCloseParticipants = React.useCallback(() => {
    setIsParticipantsOpen(false);
  }, []);

  // Adicionar/remover classe CSS no container da videoconferência
  React.useEffect(() => {
    if (videoConferenceContainer) {
      if (isParticipantsOpen) {
        videoConferenceContainer.classList.add('lk-participants-open');
      } else {
        videoConferenceContainer.classList.remove('lk-participants-open');
      }
    }
  }, [isParticipantsOpen, videoConferenceContainer]);

  if (!target) return null;

  return (
    <>
      {createPortal(
        <ParticipantsButton onClick={handleToggleParticipants} isPressed={isParticipantsOpen} />,
        target,
      )}

      {videoConferenceContainer &&
        isParticipantsOpen &&
        createPortal(
          <ParticipantsList isOpen={isParticipantsOpen} onClose={handleCloseParticipants} />,
          videoConferenceContainer,
        )}
    </>
  );
}
